import type { ClaimMode, NewDisplayClaimInput, Orientation, PairingClaimRequest } from '@/types/api'
import { isCompleteCode } from './code'

/**
 * Pairing wizard state machine.
 *
 *   code ──▶ mode ──▶ details (new)  ──▶ success
 *                 └─▶ target  (attach/replace) ──▶ success
 *
 * When opened from a display ("Pair a player" / "Replace player") the mode and display
 * are preset and locked, so the mode step is skipped.
 */
export type PairingStep = 'code' | 'mode' | 'details' | 'target' | 'success'

export interface NewDisplayDraft {
  name: string
  location_id: string | null
  group_ids: string[]
  tag_ids: string[]
  orientation: Orientation
  /** null = inherit from location / organization */
  timezone: string | null
}

export interface PairingState {
  step: PairingStep
  code: string
  nonce: string | null
  /** Set after a successful lookup. */
  lookedUp: boolean
  mode: ClaimMode | null
  /** Mode + display came from a display page and cannot be changed. */
  locked: boolean
  displayId: string | null
  reason: string
  display: NewDisplayDraft
}

export function emptyDraft(): NewDisplayDraft {
  return { name: '', location_id: null, group_ids: [], tag_ids: [], orientation: 'landscape', timezone: null }
}

export interface PairingPreset {
  code?: string | null
  nonce?: string | null
  mode?: ClaimMode | null
  displayId?: string | null
  /** Lock mode + display (launched from a display). Requires mode and displayId. */
  locked?: boolean
}

export function initialPairingState(preset: PairingPreset = {}): PairingState {
  const locked = !!(preset.locked && preset.mode && preset.mode !== 'new' && preset.displayId)
  return {
    step: 'code',
    code: preset.code ?? '',
    nonce: preset.nonce ?? null,
    lookedUp: false,
    mode: preset.mode ?? null,
    locked,
    displayId: preset.displayId ?? null,
    reason: '',
    display: emptyDraft(),
  }
}

/** Ordered steps for the current mode (used by the progress indicator). */
export function stepsFor(mode: ClaimMode | null, locked: boolean): PairingStep[] {
  const final: PairingStep = mode === 'new' ? 'details' : 'target'
  return locked ? ['code', final, 'success'] : ['code', 'mode', final, 'success']
}

/** Whether the current step's inputs allow moving forward. */
export function canAdvance(state: PairingState): boolean {
  switch (state.step) {
    case 'code':
      return isCompleteCode(state.code) && state.lookedUp
    case 'mode':
      return state.mode !== null
    case 'details':
      return state.display.name.trim().length > 0
    case 'target':
      return !!state.displayId
    case 'success':
      return false
  }
}

export function nextStep(state: PairingState): PairingStep {
  switch (state.step) {
    case 'code':
      if (state.locked) return state.mode === 'new' ? 'details' : 'target'
      return 'mode'
    case 'mode':
      return state.mode === 'new' ? 'details' : 'target'
    case 'details':
    case 'target':
      return 'success'
    case 'success':
      return 'success'
  }
}

export function previousStep(state: PairingState): PairingStep | null {
  switch (state.step) {
    case 'code':
      return null
    case 'mode':
      return 'code'
    case 'details':
    case 'target':
      return state.locked ? 'code' : 'mode'
    case 'success':
      return null
  }
}

/** The last step before success submits the claim. */
export function isSubmitStep(step: PairingStep): boolean {
  return step === 'details' || step === 'target'
}

/** Switching modes keeps the selected display only if it stays valid for the new mode. */
export function selectMode(state: PairingState, mode: ClaimMode): PairingState {
  if (state.locked) return state
  return { ...state, mode, displayId: mode === state.mode ? state.displayId : null }
}

/** `409 display-has-player` on attach → offer Replace for the same display. */
export function switchToReplace(state: PairingState): PairingState {
  return { ...state, mode: 'replace', step: 'target' }
}

export function buildClaimRequest(state: PairingState): PairingClaimRequest {
  const base = { code: state.code, nonce: state.nonce }
  if (state.mode === 'new') {
    const d = state.display
    const display: NewDisplayClaimInput = {
      name: d.name.trim(),
      location_id: d.location_id,
      group_ids: d.group_ids,
      tag_ids: d.tag_ids,
      orientation: d.orientation,
      timezone: d.timezone,
    }
    return { mode: 'new', ...base, display }
  }
  if (!state.displayId) throw new Error('A display must be selected')
  if (state.mode === 'attach') return { mode: 'attach', ...base, display_id: state.displayId }
  if (state.mode === 'replace') {
    const reason = state.reason.trim()
    return { mode: 'replace', ...base, display_id: state.displayId, reason: reason || null }
  }
  throw new Error('Choose how to use this player')
}
